import { inject, Injectable } from '@angular/core';
import { Observable, Observer } from 'rxjs';
import { InsightsService } from '@maximizer/core/shared/insights';
import {
  ContextService,
  GlobalStore,
} from '@maximizer/core/shared/data-access';
import {
  GlobalServicesLoginDetails,
  Session,
} from '@maximizer/core/shared/domain';
import {
  constructForm,
  constructAppDirectoryParams,
  constructAppDirectoryParamsCore,
  InsightsLogDetails,
  logCustomEventDetailsToInsights,
  OutlookInsightsEvents,
} from '../utils';
import {
  APP_STORE_CONFIGURATION_URL,
  APP_STORE_OAUTH_URL,
} from '../config';

type AppStoreParameters = Session | GlobalServicesLoginDetails | null;

interface AppStoreFormDetails {
  form: HTMLFormElement;
  window: Window;
}

@Injectable()
export class AppDirectoryService {
  private readonly appStoreOAuthUrl = inject(APP_STORE_OAUTH_URL);
  private readonly appStoreConfigUrl = inject(APP_STORE_CONFIGURATION_URL);
  private readonly contextService = inject(ContextService);
  private readonly insightsService = inject(InsightsService);
  private readonly globalStore = inject(GlobalStore);

  buildAppStoreFormRequest(
    sessionOrLoginDetails: AppStoreParameters,
    isInstall = false,
  ): Observable<boolean> {
    return new Observable<boolean>((observer: Observer<boolean>) => {
      try {
        const { alias, token, tenantId } = this.contextService;
        const session = this.getSession(sessionOrLoginDetails);

        if (!session || !token) {
          this.handleError(
            'Missing app store form parameters',
            isInstall,
            observer,
          );
          return;
        }

        if (
          !this.validateRequiredParameters(
            session,
            token,
            sessionOrLoginDetails,
            tenantId,
          )
        ) {
          this.handleError(
            'Missing app store form parameters',
            isInstall,
            observer,
          );
          return;
        }

        const urlParams = this.buildUrlParameters(
          session,
          token,
          isInstall,
          sessionOrLoginDetails,
          tenantId,
          alias,
        );
        if (!urlParams) {
          this.handleError(
            'App store missing URL parameters',
            isInstall,
            observer,
          );
          return;
        }

        const formDetails = this.createAppStoreForm(
          token,
          urlParams,
          isInstall,
        );
        if (!formDetails) {
          this.handleError(
            'App store form details incomplete',
            isInstall,
            observer,
          );
          return;
        }

        this.submitFormAndComplete(formDetails, isInstall, observer);
      } catch (error) {
        this.handleError((error as Error).message, isInstall, observer);
      }
    });
  }

  private getSession(
    sessionOrLoginDetails: AppStoreParameters,
  ): Session | null {
    if (this.isSession(sessionOrLoginDetails)) {
      return sessionOrLoginDetails;
    }
    return this.globalStore.session();
  }

  private isSession(obj: AppStoreParameters): obj is Session {
    return obj !== null && typeof obj === 'object' && 'customerId' in obj;
  }

  private validateRequiredParameters(
    session: Session | null,
    token: string,
    sessionOrLoginDetails: AppStoreParameters,
    tenantId?: string,
  ): boolean {
    if (!session || !token) {
      return false;
    }

    // For standard version (non-session), validate additional parameters
    if (!this.isSession(sessionOrLoginDetails)) {
      return !!(tenantId && sessionOrLoginDetails);
    }

    return true;
  }

  private buildUrlParameters(
    session: Session,
    token: string,
    isInstall: boolean,
    sessionOrLoginDetails: AppStoreParameters,
    tenantId?: string,
    alias?: string,
  ): string | undefined {
    if (this.isSession(sessionOrLoginDetails)) {
      return constructAppDirectoryParamsCore(
        session,
        token,
        isInstall,
        this.insightsService,
      );
    }

    if (!tenantId || !sessionOrLoginDetails) {
      return undefined;
    }

    return constructAppDirectoryParams(
      session,
      alias ?? '',
      token,
      tenantId,
      sessionOrLoginDetails,
      isInstall,
      this.insightsService,
    );
  }

  private createAppStoreForm(
    token: string,
    urlParams: string,
    isInstall: boolean,
  ): AppStoreFormDetails | null {
    const appStoreUrl = isInstall
      ? this.appStoreOAuthUrl
      : this.appStoreConfigUrl;

    const formDetails = constructForm(
      token,
      urlParams,
      appStoreUrl,
      this.insightsService,
    );

    return formDetails || null;
  }

  private submitFormAndComplete(
    formDetails: AppStoreFormDetails,
    isInstall: boolean,
    observer: Observer<boolean>,
  ): void {
    try {
      this.submitForm(formDetails.form, formDetails.window);
      this.logSuccessEvent(isInstall);
      observer.next(true);
      observer.complete();
    } catch (error) {
      this.handleError((error as Error).message, isInstall, observer);
    } finally {
      this.cleanupForm(formDetails.form, formDetails.window);
    }
  }

  private submitForm(form: HTMLFormElement, window: Window): void {
    window.document.body.appendChild(form);
    form.submit();
    window.focus();
  }

  private cleanupForm(form: HTMLFormElement, window: Window): void {
    if (form.parentNode) {
      window.document.body.removeChild(form);
    }
  }

  private handleError(
    errorMessage: string,
    isInstall: boolean,
    observer: Observer<boolean>,
  ): void {
    const eventName = this.getErrorEventName(isInstall, errorMessage);

    this.logErrorEvent(eventName, errorMessage);
    observer.error(new Error(errorMessage));
  }

  private getErrorEventName(isInstall: boolean, errorMessage: string): string {
    const prefix = isInstall ? 'Install-AppStore' : 'Settings-AppStore';

    if (errorMessage.includes('parameters')) {
      return `${prefix}-Params`;
    }
    if (errorMessage.includes('URL')) {
      return `${prefix}-Missing-Url-Params-Error`;
    }
    return `${prefix}-Form-Submit-Error`;
  }

  private logSuccessEvent(isInstall: boolean): void {
    const eventName = isInstall
      ? 'Install-AppStore-Form-Success'
      : 'Settings-AppStore-Form-Success';

    this.logEvent(eventName);
  }

  private logErrorEvent(eventName: string, errorMessage: string): void {
    this.logEvent(eventName, errorMessage);
    console.error(
      `An error occurred opening the App Store page: ${errorMessage}`,
    );
  }

  private logEvent(eventName: string, error?: string): void {
    const event: InsightsLogDetails = {
      insights: this.insightsService,
      name: eventName,
      eventId: OutlookInsightsEvents.AppStoreRequest,
      token: this.contextService.token,
      alias: this.globalStore.session()?.alias,
      ...(error && { error }),
    };

    logCustomEventDetailsToInsights(event);
  }
}
